import React, { useMemo, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import BlockIcon from '@mui/icons-material/Block';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { useConfirmDialog } from '~/components/Common/Providers/ConfirmDialogProvider';

import {
  useCreateGHADiscountExclusion,
  useGHADiscountExclusions,
  useUpdateGHADiscountExclusion,
} from '~/hooks/apis/connection-google/useGHADiscountExclusions';

import type { GHADiscountExclusion, GHADiscountRuleAccountType } from '~/services/ConnectionGoogleService';

import AttributionCell from './AttributionCell';
import ExclusionDialog from './ExclusionDialog';
import { ACCOUNT_LABELS, GHA_DISCOUNT_ACCOUNT_TYPE_OPTIONS } from './constants';
import { describeExclusion, formatScopeCriteria } from './utils';

// Vetoes are order-free (any match suppresses the leg) and mint no tokens, so unlike scope rules
// they are freely editable in place and the enabled switch is a real pause/unpause.
export default function ExclusionsPanel({ focusedRuleId }: { focusedRuleId?: string | null }) {
  const [accountFilter, setAccountFilter] = useState<'' | GHADiscountRuleAccountType>('');
  const [showDisabled, setShowDisabled] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [ruleToEdit, setRuleToEdit] = useState<GHADiscountExclusion | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const showConfirmDialog = useConfirmDialog();

  const { data, isLoading, error } = useGHADiscountExclusions({
    accountType: accountFilter === '' ? undefined : accountFilter,
    enabled: showDisabled ? undefined : true,
    limit: 100,
  });
  const createMutation = useCreateGHADiscountExclusion();
  const updateMutation = useUpdateGHADiscountExclusion();

  const rules = useMemo(() => {
    return [...(data?.result ?? [])].sort(
      (a, b) =>
        Number(b.enabled) - Number(a.enabled) ||
        a.accountType.localeCompare(b.accountType) ||
        a.createdAt.localeCompare(b.createdAt),
    );
  }, [data]);

  const closeDialog = () => {
    setDialogOpen(false);
    setRuleToEdit(null);
    setSubmitError(null);
  };

  if (isLoading) {
    return (
      <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={20} />
      </Box>
    );
  }
  if (error) {
    return <Alert severity="error">Failed to load exclusions</Alert>;
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="exclusions-account-filter">Account</InputLabel>
            <Select
              labelId="exclusions-account-filter"
              label="Account"
              value={accountFilter}
              onChange={(event) => setAccountFilter(event.target.value)}
            >
              <MenuItem value="">All accounts</MenuItem>
              {GHA_DISCOUNT_ACCOUNT_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={<Switch size="small" checked={showDisabled} onChange={(_, checked) => setShowDisabled(checked)} />}
            label="Show disabled"
          />
        </Stack>
        <Button variant="contained" color="warning" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
          New exclusion
        </Button>
      </Stack>

      <Alert severity="info" sx={{ mb: 2 }}>
        An exclusion vetoes every matching leg before any grant is considered — property-level rules included. A single
        property cannot be exempted from a matching exclusion; narrow the exclusion's scope instead.
      </Alert>

      {data != null && data.total > rules.length && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Showing {rules.length} of {data.total} exclusions — narrow the account filter to see the rest.
        </Alert>
      )}

      {rules.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No exclusions.
        </Typography>
      ) : (
        <Table size="small" sx={{ tableLayout: 'fixed', '& td, & th': { py: 1 } }}>
          <TableHead>
            <TableRow sx={{ '& th': { whiteSpace: 'nowrap' } }}>
              <TableCell sx={{ width: '11%' }}>Account</TableCell>
              <TableCell sx={{ width: '20%' }}>Exclusion rule</TableCell>
              <TableCell sx={{ width: '30%' }}>Description</TableCell>
              <TableCell sx={{ width: '7%' }} align="center">
                Enabled
              </TableCell>
              <TableCell sx={{ width: '22%' }}>Created / updated</TableCell>
              <TableCell align="right" sx={{ width: '10%' }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rules.map((rule) => (
              <TableRow
                key={rule.id}
                sx={{
                  ...(rule.id === focusedRuleId && { backgroundColor: 'action.selected' }),
                  ...(rule.enabled ? {} : { opacity: 0.55 }),
                }}
              >
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {ACCOUNT_LABELS[rule.accountType] ?? rule.accountType}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    variant="outlined"
                    color="warning"
                    icon={<BlockIcon />}
                    label={formatScopeCriteria(rule)}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {describeExclusion(rule)}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Switch
                    size="small"
                    checked={rule.enabled}
                    disabled={updateMutation.isPending}
                    onChange={async (_, checked) => {
                      // Turning a veto OFF widens discounting on Google immediately for every
                      // matching property — the destructive direction, so it gets a confirm.
                      if (!checked) {
                        const confirmed = await showConfirmDialog({
                          title: 'Disable exclusion?',
                          description: `Every leg this veto blocks starts discounting again immediately: ${describeExclusion(rule)}.`,
                        });
                        if (!confirmed) {
                          return;
                        }
                      }
                      updateMutation.mutate({ id: rule.id, input: { enabled: checked } });
                    }}
                  />
                </TableCell>
                <TableCell>
                  <AttributionCell
                    createdBy={rule.createdBy}
                    updatedBy={rule.updatedBy}
                    createdAt={rule.createdAt}
                    updatedAt={rule.updatedAt}
                  />
                </TableCell>
                <TableCell align="right">
                  <Button
                    size="small"
                    onClick={() => {
                      setRuleToEdit(rule);
                      setDialogOpen(true);
                    }}
                  >
                    Edit
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <ExclusionDialog
        open={dialogOpen}
        initialRule={ruleToEdit}
        isSaving={createMutation.isPending || updateMutation.isPending}
        submitError={submitError}
        onClose={closeDialog}
        onCreate={async (input) => {
          setSubmitError(null);
          try {
            await createMutation.mutateAsync(input);
            closeDialog();
          } catch (submitException) {
            setSubmitError(
              submitException instanceof Error ? submitException.message : 'Failed to create the exclusion',
            );
          }
        }}
        onUpdate={async (id, input) => {
          setSubmitError(null);
          try {
            await updateMutation.mutateAsync({ id, input });
            closeDialog();
          } catch (submitException) {
            setSubmitError(
              submitException instanceof Error ? submitException.message : 'Failed to update the exclusion',
            );
          }
        }}
      />
    </Box>
  );
}
